import isNullish from "utils/isNullish";
import { isNameTaken, MAX_NAME_LENGTH } from "utils/nameUniqueness";

import { columnKey, toColumnRef } from "./reviewDraft";

export const GROUP_OP = {
  rename: "RENAME_GROUP",
  duplicate: "DUPLICATE_GROUP",
  remove: "REMOVE_GROUP",
};

const GROUP_REF_PREFIX = "g:";

export const GROUP_NAME_IN_USE_TEXT =
  "A group with this name is already used on this shred.";

export const GROUP_NAME_TOO_LONG_TEXT = `Group names can be up to ${MAX_NAME_LENGTH} characters.`;

export const isGroupOp = (op) => Object.values(GROUP_OP).includes(op?.op);

export const groupKey = (group) => group.clientKey ?? group.id;

export const toGroupRef = (key) =>
  typeof key === "string" && key.startsWith("k:")
    ? key
    : `${GROUP_REF_PREFIX}${key}`;

export const groupRefOf = (group) => toGroupRef(groupKey(group));

export const toGroupErrorKey = (groupRef) => {
  if (groupRef?.startsWith(GROUP_REF_PREFIX)) {
    return Number(groupRef.slice(GROUP_REF_PREFIX.length));
  }
  return groupRef ?? null;
};

const isSameKey = (first, second) => String(first) === String(second);

export const isUnsavedGroup = (group) => !isNullish(group?.clientKey);

export const rootGroupId = (group) => group.duplicatedFromId ?? group.id;

export const findGroupByKey = (groups, key) =>
  groups.find((group) => isSameKey(groupKey(group), key)) ?? null;

const findGroupByRef = (groups, ref) =>
  groups.find((group) => groupRefOf(group) === ref) ?? null;

export const groupMembers = (columns, group) =>
  columns.filter(
    (column) =>
      !isNullish(column.groupId) && isSameKey(column.groupId, groupKey(group))
  );

const columnRefOf = (column) => toColumnRef(columnKey(column));

const rootOfColumnGroup = (column, groups) => {
  if (isNullish(column.groupId)) {
    return null;
  }
  const group = findGroupByKey(groups, column.groupId);
  return group ? rootGroupId(group) : null;
};

const columnInsertionPoint = (columns, groups, rootId) => {
  const last = columns.findLastIndex(
    (column) => rootOfColumnGroup(column, groups) === rootId
  );
  return last === -1 ? columns.length : last + 1;
};

const groupInsertionPoint = (groups, rootId) => {
  const last = groups.findLastIndex((group) => rootGroupId(group) === rootId);
  return last === -1 ? groups.length : last + 1;
};

const savedValuesSource = (column) =>
  isNullish(column.clientKey) ? column.id : column.valuesFromColumnId;

const toGroupColumnCopy = (member, copy, op) => ({
  ...member,
  id: null,
  clientKey: copy.clientKey,
  name: copy.name,
  groupId: op.clientKey,
  pinned: false,
  duplicatedFromId: null,
  valuesFromColumnId: op.copyValues ? savedValuesSource(member) : null,
});

const applyRenameGroup = (state, { groupRef, name }) => ({
  ...state,
  groups: state.groups.map((group) =>
    groupRefOf(group) === groupRef ? { ...group, name } : group
  ),
});

const applyDuplicateGroup = (state, op) => {
  const source = state.groups.find(({ id }) => id === op.sourceGroupId);
  if (!source) {
    return state;
  }
  const rootId = rootGroupId(source);
  const copies = op.columns
    .map((copy) => {
      const member = state.columns.find(
        (column) => columnRefOf(column) === copy.sourceColumnRef
      );
      return member ? toGroupColumnCopy(member, copy, op) : null;
    })
    .filter(Boolean);
  const group = {
    ...source,
    id: null,
    clientKey: op.clientKey,
    name: op.name,
    color: op.color ?? source.color,
    duplicatedFromId: rootId,
  };
  const columnPoint = columnInsertionPoint(state.columns, state.groups, rootId);
  const groupPoint = groupInsertionPoint(state.groups, rootId);
  return {
    columns: [
      ...state.columns.slice(0, columnPoint),
      ...copies,
      ...state.columns.slice(columnPoint),
    ],
    groups: [
      ...state.groups.slice(0, groupPoint),
      group,
      ...state.groups.slice(groupPoint),
    ],
  };
};

const applyRemoveGroup = (state, { groupRef }) => {
  const group = findGroupByRef(state.groups, groupRef);
  if (!group) {
    return state;
  }
  const key = groupKey(group);
  return {
    columns: state.columns.filter(
      (column) => isNullish(column.groupId) || !isSameKey(column.groupId, key)
    ),
    groups: state.groups.filter((other) => other !== group),
  };
};

export const GROUP_APPLY_BY_OP = {
  [GROUP_OP.rename]: applyRenameGroup,
  [GROUP_OP.duplicate]: applyDuplicateGroup,
  [GROUP_OP.remove]: applyRemoveGroup,
};

export const getShredGroupNameError = (name, groups, ownKey = null) => {
  const trimmed = (name ?? "").trim();
  if (!trimmed) {
    return null;
  }
  if (trimmed.length > MAX_NAME_LENGTH) {
    return GROUP_NAME_TOO_LONG_TEXT;
  }
  const otherNames = groups
    .filter((group) => ownKey === null || !isSameKey(groupKey(group), ownKey))
    .map(({ name: otherName }) => otherName);
  return isNameTaken(trimmed, otherNames) ? GROUP_NAME_IN_USE_TEXT : null;
};

export const getGroupActions = (group) => ({
  rename: Boolean(group.renamable),
  duplicate: Boolean(group.repeatable) && !isUnsavedGroup(group),
  remove: !isNullish(group.duplicatedFromId),
});
